import React from 'react';
import Joi from 'joi';
import _ from 'underscore';
import Cookies from 'cookies';
import getConfig from 'next/config';
import { api as apiRequest } from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import {
	checkEntityStatus,
	getFullEntityUrl
} from '@culture/common-helpers/entities';
import { getMovieMicrodata } from '@culture/common-helpers/microdata/movie';

import { getEntityRubric } from 'utils/helpers/rubrics';
import { collectVisitedEntitiesInCookies } from 'utils/helpers/recommendations';
import {
	getIsWasBroadcast,
	getMovieHasNationalProjectCultureTag
} from 'utils/helpers/movies';
import { getBreadcrumbs } from 'logic/page';
import Template from 'pageTemplates/entities/broadcast';

const { serverRuntimeConfig } = getConfig();

const LiveDetail = (props) => <Template {...props} />;

const getNearestBroadcastParams = (cleanCache, broadcastType, excludeIds) =>
	addCleanCache(
		{
			statuses: ['published'],
			types: [broadcastType],
			isPassed: true,
			fields: [
				'title',
				'name',
				'thumbnailFile',
				'publishDate',
				'institute',
				'rubrics',
				'type'
			].join(),
			sort: '-publishDate',
			limit: 10,
			cached: true,
			isWasBroadcast: true,
			excludeIds
		},
		cleanCache
	);

const getInstituteBroadcastParams = (cleanCache, instituteId, excludeIds) =>
	addCleanCache(
		{
			statuses: ['published'],
			isPassed: true,
			institutes: [instituteId],
			fields: [
				'title',
				'name',
				'thumbnailFile',
				'publishDate',
				'institute',
				'rubrics',
				'type'
			].join(),
			sort: '-publishDate',
			limit: 10,
			cached: true,
			isWasBroadcast: true,
			excludeIds
		},
		cleanCache
	);

const getBreadcrumbsParams = (cleanCache, movie) =>
	addCleanCache(
		{
			entityType: 'movies',
			_id: movie._id,
			rubricPath: (movie.rubric && movie.rubric.path) || '',
			cached: true
		},
		cleanCache
	);

const getNearestBroadcasts = (broadcast, instituteBroadcasts, params) => {
	if (broadcast.broadcastType) {
		let excludeIds = [broadcast._id];

		if (instituteBroadcasts.items.length) {
			const ids = _(instituteBroadcasts.items).pluck('_id');

			excludeIds = [...excludeIds, ...ids];
		}

		return apiRequest(
			'broadcast',
			getNearestBroadcastParams(
				params.cleanCache,
				broadcast.broadcastType,
				excludeIds
			)
		);
	}

	return { items: [] };
};

const getInstituteBroadcasts = (broadcast, params) => {
	if (broadcast.institute) {
		return apiRequest(
			'broadcast',
			getInstituteBroadcastParams(params.cleanCache, broadcast.institute._id, [
				broadcast._id
			])
		);
	}

	return { items: [] };
};

const getBroadcast = (params) => {
	const broadcastParams = addCleanCache(
		{
			expand: 'mainRubric,rubrics,materials,institute',
			withEntityMeta: true,
			cached: true
		},
		params.cleanCache
	);

	return apiRequest(`broadcast/${params._id}`, broadcastParams);
};

export async function getServerSideProps({ query, req, res, resolvedUrl }) {
	const validationSchema = Joi.object().keys({
		_id: Joi.number().integer().required(),
		name: Joi.array().items(Joi.string()).length(1),
		cleanCache: Joi.boolean()
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const broadcast = await getBroadcast(params);

	if (
		getIsWasBroadcast(broadcast) &&
		getMovieHasNationalProjectCultureTag(broadcast.tags)
	) {
		const url = `${serverRuntimeConfig.basePath || ''}${resolvedUrl.replace(
			'broadcast',
			'movies'
		)}`;

		if (url) {
			res.redirect(301, url);
			return {};
		}
	}

	if (!checkEntityStatus(broadcast)) {
		return {
			notFound: true
		};
	}

	if (!params.name || broadcast.name !== params.name[0]) {
		return {
			redirect: {
				destination: getFullEntityUrl(req.url, 'broadcast', broadcast),
				permanent: true
			}
		};
	}

	if (broadcast?._id) {
		const cookies = new Cookies(req, res);
		collectVisitedEntitiesInCookies(cookies, ['broadcasts'], broadcast._id);
	}

	const rubric = getEntityRubric(broadcast);

	const instituteBroadcasts = await getInstituteBroadcasts(broadcast, params);

	const breadcrumbs = await getBreadcrumbs(
		getBreadcrumbsParams(params.cleanCache, broadcast)
	);

	const nearestBroadcasts = await getNearestBroadcasts(
		broadcast,
		instituteBroadcasts,
		params
	);

	res.setHeader('Cache-Control', serverRuntimeConfig.cacheControl.smallAge);

	return {
		props: {
			broadcast,
			microdata: getMovieMicrodata(broadcast, 'broadcast'),
			breadcrumbs,
			nearestBroadcasts: nearestBroadcasts.items,
			instituteBroadcasts: instituteBroadcasts.items,
			rubric
		}
	};
}

export default LiveDetail;
